import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import { Loader, SuccessModal } from '@ohif/ui';
import { customStyle } from '@custom/Utils/utils';
import { Formik, Form, ErrorMessage, FieldArray } from 'formik';
import { PricingFormSchema } from './schema';
import { updatePlans } from '@custom/rtk-apis/Setting/plans/updatePlans';
import { getPlans } from '@custom/rtk-apis/Setting/plans/getPlans';
import { resetSuccess as resetUpdateSuccess } from '@custom/store/slices/Setting/plans/updatePlanSlice';
import { resetSuccess as resetGetSuccess } from '@custom/store/slices/Setting/plans/getPlanSlice';
import { resetError as resetUpdateError } from '@custom/store/slices/Setting/plans/updatePlanSlice';
import { resetError as resetGetError } from '@custom/store/slices/Setting/plans/getPlanSlice';
import { Plan, PlanConfig } from '@custom/store/slices/Setting/plans/getPlanSlice';

const emptyPlan = {
  name: '',
  monthly: {
    centers: 0,
    radiologists: 0,
    casesPerMonth: 0,
    extraCaseRate: 0,
    cloudStorage: 0,
    showPublic: false,
    teleradiology: false,
    telepathology: false,
    speechToText: false,
    aiServices: false,
    customFeatures: []
  },
  yearly: {
    centers: 0,
    radiologists: 0,
    casesPerMonth: 0,
    extraCaseRate: 0,
    cloudStorage: 0,
    showPublic: false,
    teleradiology: false,
    telepathology: false,
    speechToText: false,
    aiServices: false,
    customFeatures: []
  }
};

const defaultFormFeatureList = [
  { name: 'Speech-to-Text', id: 'speechToText'},
  { name: 'AI Services', id: 'aiServices'},
]

const AddEditPlans: React.FC = ({formPlans, activeDurations, setActiveDurations, setIsEditMode}) => {
  const dispatch = useDispatch<AppDispatch>();

  const getPlansResponse = useSelector((state: RootState) => state.getPlansResponse);
  const updatePlansResponse = useSelector((state: RootState) => state.updatePlansResponse);
  const [removedPlans, setRemovedPlans] = useState([]);
  const [successModal, setSuccessModal] = useState(false);

  // useEffect(() => {
  //   dispatch(getPlans());
  // }, [dispatch]);

  // useEffect(() => {
  //   if (getPlansResponse?.success && getPlansResponse?.plans?.data?.length > 0) {
  //     // Handle potential differences in data structure from API
  //     const updatedPlans = getPlansResponse?.plans?.data?.map((plan: any) => {
  //       // If the plan already has monthly and yearly configs, use them directly
  //       if (plan.monthly && plan.yearly) {
  //         return plan;
  //       }

  //       // If the plan is in the old format, convert it to the new format
  //       // by copying the old fields to both monthly and yearly
  //       if (!plan.monthly && !plan.yearly) {
  //         const { name, _id, ...otherProps } = plan;
  //         return {
  //           _id,
  //           name,
  //           monthly: { ...otherProps },
  //           yearly: {
  //             ...otherProps,
  //             // Adjust yearly values if needed
  //             extraCaseRate: Math.max(1, otherProps.extraCaseRate * 0.8), // 20% discount for yearly
  //             cloudStorage: otherProps.cloudStorage.replace('1 Month', '12 Months')
  //           }
  //         };
  //       }

  //       return plan;
  //     });

  //     setFormPlans(updatedPlans);
  //     setRemovedPlans([]);

  //     // Initialize active durations for all plans
  //     setActiveDurations(
  //       updatedPlans?.reduce((acc, _, index) => ({ ...acc, [index]: 'monthly' }), {})
  //     );
  //   }
  // }, [getPlansResponse]);

  useEffect(() => {
    if (updatePlansResponse?.success) {
      setSuccessModal(true);
      setTimeout(() => {
        setSuccessModal(false);
        dispatch(resetUpdateSuccess());
        dispatch(getPlans());
        setIsEditMode((prev) => !prev);
      }, 2000);
    }
  }, [updatePlansResponse]);

  const handleSubmit = (values: { plans }) => {
    const updatedRemovedPlans = removedPlans.map((plan) => ({ ...plan, delete: true }));
    const payload = [...values.plans, ...updatedRemovedPlans];
    dispatch(updatePlans(payload));
  };

  return (
    <div className="details">
      <div className="form form--innersec">
        <section className="form__section">
          <h1 className='form__section__title'>Plan Settings</h1>
          <Formik
            initialValues={{ plans: formPlans.length > 0 ? formPlans : [emptyPlan] }}
            validationSchema={PricingFormSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              errors,
              values,
              handleChange,
              handleBlur,
              setFieldValue,
              touched,
              isValid,
              dirty
            }) => (
              <Form>
                <FieldArray name="plans">
                  {({ remove, push }) => (
                    <>
                      {values.plans.map((plan, index) => (
                        <div key={plan._id || index}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h2 className='form__section__title'>{plan.name || 'New Plan'}</h2>
                            {/* Only show remove button if there are more than 1 plan */}
                            {values.plans.length > 1 && (
                              <button
                                type="button"
                                className="btn btn__danger remove-plan-btn"
                                onClick={() => {
                                      if(plan._id) {
                                    setRemovedPlans((prev) => [...prev, plan]);
                                  }
                                  remove(index);
                                }}
                              >
                                Remove Plan
                              </button>
                            )}
                          </div>

                          {/* Monthly/Yearly Duration Tabs */}
                          <div className="plan-duration-tabs">
                            <button
                              type="button"
                              className={`plan-duration-tab ${activeDurations[index] === 'monthly' ? 'active' : ''}`}
                              onClick={() => {
                                const newDurations = { ...activeDurations };
                                newDurations[index] = 'monthly';
                                setActiveDurations(newDurations);
                              }}
                            >
                              Monthly Plan
                            </button>
                            <button
                              type="button"
                              className={`plan-duration-tab ${activeDurations[index] === 'yearly' ? 'active' : ''}`}
                              onClick={() => {
                                const newDurations = { ...activeDurations };
                                newDurations[index] = 'yearly';
                                setActiveDurations(newDurations);
                              }}
                            >
                              Yearly Plan
                            </button>
                          </div>

                          <div className="plan-config-container">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                              <label className="form__label" htmlFor={`plans.${index}.${activeDurations[index]}.showPublic`}>
                                Show on public plans page
                              </label>
                              <div className="toggle-switch" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <input
                                  id={`plans.${index}.${activeDurations[index]}.showPublic`}
                                  name={`plans.${index}.${activeDurations[index]}.showPublic`}
                                  type="checkbox"
                                  onChange={handleChange}
                                  checked={values.plans[index][activeDurations[index]]?.showPublic}
                                  className="toggle-input"
                                  aria-label="Toggle visibility on public plans page"
                                />
                                <label className="toggle-slider" htmlFor={`plans.${index}.${activeDurations[index]}.showPublic`}></label>
                              </div>
                            </div>
                            <div className="formRow formRow__4">
                              <div className="formCol">
                                <div className="form__Field">
                                  <label className="form__label" htmlFor={`plans.${index}.name`}>
                                    Plan Name
                                    <span className="required-asterisk">*</span>
                                  </label>
                                  <input
                                    id={`plans.${index}.name`}
                                    name={`plans.${index}.name`}
                                    type="text"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.plans[index].name}
                                    className="form__input form__input--sm"
                                  />
                                  <ErrorMessage name={`plans.${index}.name`} component="div" className="error text-error" />
                                </div>
                              </div>
                              <div className="formCol">
                                <div className="form__Field">
                                  <label className="form__label" htmlFor={`plans.${index}.planDesc`}>
                                    Plan Description
                                    <span className="required-asterisk">*</span>
                                  </label>
                                  <input
                                    id={`plans.${index}.planDesc`}
                                    name={`plans.${index}.planDesc`}
                                    type="text"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.plans[index].planDesc}
                                    className="form__input form__input--sm"
                                  />
                                  <ErrorMessage name={`plans.${index}.planDesc`} component="div" className="error text-error" />
                                </div>
                              </div>
                              <div className="formCol">
                                <div className="form__Field">
                                  <label className="form__label" htmlFor={`plans.${index}.${activeDurations[index]}.planPrice`}>
                                    Plan Price
                                    <span className="required-asterisk">*</span>
                                  </label>
                                  <input
                                    id={`plans.${index}.${activeDurations[index]}.planPrice`}
                                    name={`plans.${index}.${activeDurations[index]}.planPrice`}
                                    type="text"
                                    onChange={handleChange}
                                    onFocus={(e) => {
                                      if (e.target.value === '0') {
                                        e.target.value = '';
                                      }
                                    }}
                                    onBlur={handleBlur}
                                    value={values.plans[index][activeDurations[index]]?.planPrice}
                                    className="form__input form__input--sm"
                                  />
                                  <ErrorMessage name={`plans.${index}.${activeDurations[index]}.planPrice`} component="div" className="error text-error" />
                                </div>
                              </div>
                              <div className="formCol">
                                <div className="form__Field">
                                  <label className="form__label" htmlFor={`plans.${index}.${activeDurations[index]}.planCredits`}>
                                    Plan Credits
                                    <span className="required-asterisk">*</span>
                                  </label>
                                  <input
                                    id={`plans.${index}.${activeDurations[index]}.planCredits`}
                                    name={`plans.${index}.${activeDurations[index]}.planCredits`}
                                    type="text"
                                    onChange={handleChange}
                                    onFocus={(e) => {
                                      if (e.target.value === '0') {
                                        e.target.value = '';

                                      }
                                    }}
                                    onBlur={handleBlur}
                                    value={values.plans[index][activeDurations[index]]?.planCredits}
                                    className="form__input form__input--sm"
                                  />
                                  <ErrorMessage name={`plans.${index}.${activeDurations[index]}.planCredits`} component="div" className="error text-error" />
                                </div>
                              </div>
                              {/* <div className="formCol">
                                    <div className="form__Field">
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1.8rem' }}>
                                        <label className="form__label" htmlFor={`plans.${index}.recommended`}>
                                          Mark as recommended plan
                                        </label>
                                        <div className="toggle-switch" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                          <input
                                            id={`plans.${index}.recommended`}
                                            name={`plans.${index}.recommended`}
                                            type="checkbox"
                                            onChange={(e) => {
                                              // If this plan is being set as recommended, unset any other plans
                                              if (e.target.checked) {
                                                const newValues = [...values.plans];
                                                newValues.forEach((p, i) => {
                                                  if (i !== index) {
                                                    newValues[i].recommended = false;
                                                  }
                                                });
                                                newValues[index].recommended = true;

                                                // Set the field value manually since we're updating multiple values
                                                values.plans = newValues;
                                              } else {
                                                handleChange(e);
                                              }
                                            }}
                                            checked={values.plans[index].recommended || false}
                                            className="toggle-input"
                                            aria-label="Mark this plan as recommended"
                                          />
                                          <label className="toggle-slider" htmlFor={`plans.${index}.recommended`}></label>
                                        </div>
                                      </div>
                                    </div>
                                  </div> */}
                            </div>
                            <div className='font-semibold mb-2'>Features: </div>
                            <div className="formRow formRow__4">
                              <div className="formCol">
                                <div className="form__Field">
                                  <label className="form__label" htmlFor={`plans.${index}.${activeDurations[index]}.users`}>
                                    Users
                                    <span className="required-asterisk">*</span>
                                  </label>
                                  <input
                                    id={`plans.${index}.${activeDurations[index]}.users`}
                                    name={`plans.${index}.${activeDurations[index]}.users`}
                                    type="number"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    onFocus={(e) => {
                                      if (e.target.value === '0') {
                                        e.target.value = '';
                                      }
                                    }}
                                    value={values.plans[index][activeDurations[index]]?.users}
                                    className="form__input form__input--sm"
                                  />
                                  <ErrorMessage name={`plans.${index}.${activeDurations[index]}.users`} component="div" className="error text-error" />
                                </div>
                              </div>
                              <div className="formCol">
                                <div className="form__Field">
                                  <label className="form__label" htmlFor={`plans.${index}.${activeDurations[index]}.cloudStorage`}>
                                    Cloud Storage (GB)
                                    <span className="required-asterisk">*</span>
                                  </label>
                                  <input
                                    id={`plans.${index}.${activeDurations[index]}.cloudStorage`}
                                    name={`plans.${index}.${activeDurations[index]}.cloudStorage`}
                                    type="number"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    onFocus={(e) => {
                                      if (e.target.value === '0') {
                                        e.target.value = '';
                                      }
                                    }}
                                    value={values.plans[index][activeDurations[index]]?.cloudStorage}
                                    className="form__input form__input--sm"
                                  />
                                  <ErrorMessage name={`plans.${index}.${activeDurations[index]}.cloudStorage`} component="div" className="error text-error" />
                                </div>
                              </div>
                              <div className='form__checkbox form__checkbox--end'>
                                {
                                  defaultFormFeatureList?.map((feature) => {
                                    return <div className="form__checkbox__option">
                                      <input
                                        type="checkbox"
                                        id={`plans.${index}.${activeDurations[index]}.${feature?.id}`}
                                        className="form__checkbox__input"
                                        name={`plans.${index}.${activeDurations[index]}.${feature?.id}`}
                                        checked={values.plans[index][activeDurations[index]]?.[feature?.id] || false}
                                        onChange={(e) => {
                                          const newPlans = values.plans.map((plan, planIndex) => {
                                            if (planIndex === index) {
                                              return {
                                                ...plan,
                                                [activeDurations[index]]: {
                                                  ...plan[activeDurations[index]],
                                                  [feature?.id]: e.target.checked
                                                }
                                              };
                                            }
                                            return plan;
                                          });
                                          setFieldValue('plans', newPlans);
                                        }}
                                      />
                                      <label className="form__checkbox__label" htmlFor={feature?.id} >
                                        {feature?.name}
                                      </label>
                                    </div>
                                  })
                                }
                              </div>
                            </div>

                            {/* Custom Features */}
                            <div className="form__Field" style={{ marginTop: '1rem' }}>
                              <FieldArray
                                name={`plans.${index}.${activeDurations[index]}.customFeatures`}
                                render={arrayHelpers => (
                                  <div>
                                    {values.plans[index][activeDurations[index]]?.customFeatures?.map((feature, featureIndex) => (
                                      <div key={featureIndex} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                                        <input
                                          type="text"
                                          className="form__input form__input--sm"
                                          style={{ flex: 1, marginRight: '0.5rem' }}
                                          value={feature}
                                          onChange={(e) => {
                                            const newFeatures = [...values.plans[index][activeDurations[index]].customFeatures];
                                            newFeatures[featureIndex] = e.target.value;
                                            setFieldValue(
                                              `plans.${index}.${activeDurations[index]}.customFeatures`,
                                              newFeatures
                                            );
                                          }}
                                          placeholder="Enter feature"
                                        />
                                        <button
                                          type="button"
                                          className="btn btn__danger"
                                          style={{ padding: '0.25rem 0.5rem' }}
                                          onClick={() => arrayHelpers.remove(featureIndex)}
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    ))}
                                    <button
                                      type="button"
                                      className="btn btn__primary btn__outlined"
                                      style={{ marginTop: '0.5rem' }}
                                      onClick={() => arrayHelpers.push('')}
                                    >
                                      + Add More Features
                                    </button>
                                  </div>
                                )}
                              />
                            </div>
                          </div>

                          {index !== values.plans.length - 1 && <hr style={{ height: "1px", backgroundColor: "gray", margin: "1.5rem 0" }} />}
                        </div>
                      ))}

                      <div className="add-plan-button-container">
                        <button
                          type="button"
                          className="btn btn__secondary add-more-btn"
                          onClick={() => {
                            // Add a new plan with both monthly and yearly configurations
                            const newPlan = {
                              name: 'New Plan',
                              planDesc: '',
                              // recommended: false,
                              monthly: {
                                planPrice: 0,
                                users: 0,
                                planCredits: 0,
                                cloudStorage: 0,
                                showPublic: false,
                                speechToText: false,
                                aiServices: false,
                              },
                              yearly: {
                                planPrice: 0,
                                users: 0,
                                planCredits: 0,
                                cloudStorage: 0,
                                showPublic: false,
                                speechToText: false,
                                aiServices: false,
                              }
                            };

                            // Add the new plan to the form
                            push(newPlan);

                            // Update the active durations for the new plan
                            const newIndex = values.plans.length;
                            const newDurations = { ...activeDurations };
                            newDurations[newIndex] = 'monthly';
                            setActiveDurations(newDurations);
                          }}
                        >
                          + Add More
                        </button>
                      </div>

                      <div className="btn__group">
                        <button
                          type="submit"
                          className="btn btn__primary"
                          // onClick={() => {
                          //   console.log('Submitting form:', values, errors);
                          // }}
                          disabled={updatePlansResponse.loading || !(isValid && dirty)}
                        >
                          {updatePlansResponse.loading ? 'Updating...' : 'Update'}
                        </button>
                      </div>
                    </>
                  )}
                </FieldArray>
              </Form>
            )}
          </Formik>
        </section>
      </div>
      <Loader isLoaderOpen={updatePlansResponse.loading} title="Loading..." />

      <SuccessModal
        isSuccessModalOpen={successModal}
        title="Plans Updated Successfully"
        handleCloseModal={() => {
          setSuccessModal(false);
          dispatch(resetUpdateSuccess());
        }}
      />
    </div>
  );
};

export default AddEditPlans;
